// src/components/common/Layout/Sidebar.tsx
import {useState} from "react";
import {NavLink, useLocation} from "react-router-dom";
import {
  Home,
  Building2,
  MapPin,
  Users,
  MessageSquare,
  Mail,
  Settings,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Crown,
} from "lucide-react";
import {clsx} from "clsx";
import {ROUTES} from "../../../utils/constants";
import logo from "../../../../public/logo.jpg";

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavItem[];
  badge?: number;
}

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onMobileClose,
}) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([
    "properties",
    "locations",
  ]);

  // Sample inquiry count - in real app, get from Redux store
  const inquiryCount = 5;

  const navItems: NavItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <Home className="w-5 h-5" />,
      path: ROUTES.DASHBOARD,
    },
    {
      key: "properties",
      label: "Properties",
      icon: <Building2 className="w-5 h-5" />,
      children: [
        {
          key: "properties-list",
          label: "Property List",
          icon: <div className="w-2 h-2 bg-current rounded-full" />,
          path: ROUTES.PROPERTIES,
        },
        {
          key: "properties-create",
          label: "Add Property",
          icon: <div className="w-2 h-2 bg-current rounded-full" />,
          path: ROUTES.PROPERTY_CREATE,
        },
        {
          key: "property-types",
          label: "Property Types",
          icon: <div className="w-2 h-2 bg-current rounded-full" />,
          path: ROUTES.PROPERTY_TYPES,
        },
        {
          key: "property-contacts",
          label: "Property Contacts",
          icon: <div className="w-2 h-2 bg-current rounded-full" />,
          path: "/property-contacts",
        },
      ],
    },
    {
      key: "locations",
      label: "Locations",
      icon: <MapPin className="w-5 h-5" />,
      children: [
        {
          key: "regions",
          label: "Regions",
          icon: <div className="w-2 h-2 bg-current rounded-full" />,
          path: ROUTES.REGIONS,
        },
        {
          key: "townships",
          label: "Townships",
          icon: <div className="w-2 h-2 bg-current rounded-full" />,
          path: ROUTES.TOWNSHIPS,
        },
      ],
    },
    {
      key: "user-management",
      label: "User Management",
      path: "#",
      icon: <Users className="w-5 h-5" />,
      children: [
        {
          key: "customers",
          label: "Customers",
          path: ROUTES.CUSTOMERS,
          icon: <Users className="w-4 h-4" />,
        },
        {
          key: "admins",
          label: "Administrators",
          path: ROUTES.ADMINS,
          icon: <Crown className="w-4 h-4" />,
        },
      ],
    },
    // {
    //   key: "inquiries",
    //   label: "Inquiries",
    //   icon: <MessageSquare className="w-5 h-5" />,
    //   path: ROUTES.INQUIRIES,
    //   badge: inquiryCount,
    // },
    {
      key: "contacts",
      label: "Contacts",
      icon: <Mail className="w-5 h-5" />,
      path: ROUTES.CONTACTS,
    },
    // {
    //   key: "price-ranges",
    //   label: "Price Ranges",
    //   icon: <Settings className="w-5 h-5" />,
    //   path: ROUTES.PRICE_RANGES,
    // },
    {
      key: "banners",
      label: "Banners",
      icon: <Settings className="w-5 h-5" />, // Replace with the correct icon if needed
      path: "/banners",
    },
  ];

  const toggleExpanded = (key: string) => {
    setExpandedItems((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const isActiveRoute = (path?: string, children?: NavItem[]): boolean => {
    if (path) {
      return location.pathname === path;
    }
    if (children) {
      return children.some((child) =>
        isActiveRoute(child.path, child.children)
      );
    }
    return false;
  };

  const renderNavItem = (item: NavItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.key);
    const isActive = isActiveRoute(item.path, item.children);

    if (hasChildren) {
      return (
        <div key={item.key} className="mb-1">
          <button
            onClick={() => toggleExpanded(item.key)}
            className={clsx(
              "w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors",
              "hover:bg-gray-100 dark:hover:bg-dark-700",
              isActive &&
                "bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300",
              !isActive && "text-gray-700 dark:text-gray-300"
            )}
          >
            <div className="flex items-center">
              {item.icon}
              {!isCollapsed && <span className="ml-3">{item.label}</span>}
            </div>
            {!isCollapsed && (
              <div className="flex items-center">
                {item.badge && (
                  <span className="mr-2 px-2 py-0.5 text-xs font-medium bg-danger-100 text-danger-800 rounded-full">
                    {item.badge}
                  </span>
                )}
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </div>
            )}
          </button>

          {!isCollapsed && isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children?.map((child) => renderNavItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    // Single nav item
    const content = (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          {item.icon}
          {!isCollapsed && <span className="ml-3">{item.label}</span>}
        </div>
        {!isCollapsed && item.badge && (
          <span className="px-2 py-0.5 text-xs font-medium bg-danger-100 text-danger-800 rounded-full">
            {item.badge}
          </span>
        )}
      </div>
    );

    if (item.path) {
      return (
        <NavLink
          key={item.key}
          to={item.path}
          className={({isActive}) =>
            clsx(
              "flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors mb-1",
              "hover:bg-gray-100 dark:hover:bg-dark-700",
              isActive
                ? "bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300"
                : "text-gray-700 dark:text-gray-300",
              depth > 0 && "ml-6"
            )
          }
        >
          {content}
        </NavLink>
      );
    }

    return (
      <div
        key={item.key}
        className={clsx(
          "flex items-center px-3 py-2 text-sm font-medium rounded-lg mb-1",
          "text-gray-700 dark:text-gray-300",
          depth > 0 && "ml-6"
        )}
      >
        {content}
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center px-4 py-6 border-b border-gray-200 dark:border-dark-700">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
            <img
              src={logo}
              alt="Logo"
              className="w-8 h-8 object-contain"
            />
          </div>
          {!isCollapsed && (
            <div className="ml-3">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                Zwe Htet Real Estate
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Admin Dashboard
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => renderNavItem(item))}
      </nav>

      {/* Collapse Toggle - Desktop */}
      <div className="hidden md:block border-t border-gray-200 dark:border-dark-700 p-4">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
        >
          <Menu className="w-5 h-5" />
          {!isCollapsed && <span className="ml-3">Collapse</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={clsx(
          "hidden md:flex md:flex-col fixed left-0 top-0 h-full bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-dark-700 transition-all duration-300",
          isCollapsed ? "w-20" : "w-64"
        )}
      >
        {sidebarContent}
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={onMobileClose}
          />
          <div className="fixed left-0 top-0 h-full w-64 bg-white dark:bg-dark-800 shadow-xl">
            <div className="flex items-center justify-between px-4 py-6 border-b border-gray-200 dark:border-dark-700">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="ml-3">
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                    Zwe Htet Real Estate
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Admin Dashboard
                  </p>
                </div>
              </div>
              <button
                onClick={onMobileClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
              {navItems.map((item) => renderNavItem(item))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
